import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_STEPS } from './constants';
import { StrategyOptions } from './types';
import { getNextIndex } from './utils';
export var APIStatus = /*#__PURE__*/ function(APIStatus) {
    APIStatus["Idle"] = "idle";
    APIStatus["Loading"] = "loading";
    APIStatus["Success"] = "success";
    APIStatus["Error"] = "error";
    return APIStatus;
}({});
const INITIAL_DATA = {
    selectedStep: DEFAULT_STEPS[0],
    previousStep: DEFAULT_STEPS[0],
    steps: DEFAULT_STEPS,
    stepsData: null,
    isPlaceholderModalShown: false,
    selectedProject: null,
    selectedStrategy: StrategyOptions.LoadBalanced,
    isStrategySelectionStepShown: false,
    goToNextStepStatus: "idle",
    findTeamMembersStatus: "idle",
    assignAndAllocateStatus: "idle",
    rowsWithUsersRemovedManually: []
};
export const teamPlannerSlice = createSlice({
    initialState: INITIAL_DATA,
    name: 'teamPlanner',
    reducers: {
        setStepsData: (state, { payload })=>{
            return {
                ...state,
                stepsData: {
                    ...state.stepsData,
                    ...payload
                }
            };
        },
        updateField: (state, { payload })=>{
            var _ref;
            var _state_stepsData_rows, _state_stepsData;
            return {
                ...state,
                stepsData: {
                    ...state.stepsData,
                    rows: (_ref = (_state_stepsData = state.stepsData) === null || _state_stepsData === void 0 ? void 0 : (_state_stepsData_rows = _state_stepsData.rows) === null || _state_stepsData_rows === void 0 ? void 0 : _state_stepsData_rows.map((row)=>{
                        var _ref;
                        var _row_team, _row_user;
                        return ((_ref = (_row_team = row.team) === null || _row_team === void 0 ? void 0 : _row_team.teamId) !== null && _ref !== void 0 ? _ref : (_row_user = row.user) === null || _row_user === void 0 ? void 0 : _row_user.userId) === payload.id ? {
                            ...row,
                            [payload.field]: payload.newData
                        } : row;
                    })) !== null && _ref !== void 0 ? _ref : []
                }
            };
        },
        updateAllUnresolvedFields: (state, { payload })=>{
            var _ref;
            var _state_stepsData_rows, _state_stepsData;
            return {
                ...state,
                stepsData: {
                    ...state.stepsData,
                    rows: (_ref = (_state_stepsData = state.stepsData) === null || _state_stepsData === void 0 ? void 0 : (_state_stepsData_rows = _state_stepsData.rows) === null || _state_stepsData_rows === void 0 ? void 0 : _state_stepsData_rows.map((row)=>!row.resolved ? {
                            ...row,
                            [payload.field]: payload.newData
                        } : row)) !== null && _ref !== void 0 ? _ref : []
                }
            };
        },
        addNewPlaceholder: (state, { payload })=>{
            var _ref;
            var _payload_teams, _state_stepsData_rows, _state_stepsData;
            const newRows = (_payload_teams = payload.teams) === null || _payload_teams === void 0 ? void 0 : _payload_teams.map((team)=>({
                    team: {
                        teamId: team.teamId,
                        teamName: team.teamName,
                        roleId: team.roleId
                    },
                    dateEditable: true,
                    allocatedMinutes: 0,
                    resolved: false,
                    isNoMatchFoundShown: false
                }));
            return {
                ...state,
                stepsData: {
                    ...state.stepsData,
                    rows: (_ref = (_state_stepsData = state.stepsData) === null || _state_stepsData === void 0 ? void 0 : (_state_stepsData_rows = _state_stepsData.rows) === null || _state_stepsData_rows === void 0 ? void 0 : _state_stepsData_rows.concat(newRows)) !== null && _ref !== void 0 ? _ref : []
                }
            };
        },
        togglePlaceholderModal: (state)=>{
            return {
                ...state,
                isPlaceholderModalShown: !state.isPlaceholderModalShown
            };
        },
        setSelectedProject: (state, { payload })=>{
            return {
                ...state,
                selectedProject: payload
            };
        },
        setSelectedStrategy: (state, { payload })=>{
            return {
                ...state,
                selectedStrategy: payload
            };
        },
        setSelectedStep: (state, { payload })=>{
            return {
                ...state,
                selectedStep: payload
            };
        },
        setPreviousStep: (state, { payload })=>{
            return {
                ...state,
                previousStep: payload
            };
        },
        addSteps: (state, { payload })=>{
            return {
                ...state,
                steps: payload
            };
        },
        removeStep: (state, { payload })=>{
            return {
                ...state,
                steps: state.steps.filter((step)=>step.id !== payload.id)
            };
        },
        resetAllData: (state)=>{
            return {
                ...state,
                selectedStep: DEFAULT_STEPS[0],
                selectedStrategy: StrategyOptions.LoadBalanced,
                isStrategySelectionStepShown: true,
                selectedProject: null,
                stepsData: null,
                rowsWithUsersRemovedManually: []
            };
        },
        resetSteps: (state)=>{
            return {
                ...state,
                steps: DEFAULT_STEPS
            };
        },
        goToNextStepIdle: (state)=>{
            return {
                ...state,
                goToNextStepStatus: "idle"
            };
        },
        goToNextStepLoading: (state)=>{
            return {
                ...state,
                goToNextStepStatus: "loading"
            };
        },
        goToNextStepFailure: (state)=>{
            return {
                ...state,
                goToNextStepStatus: "error"
            };
        },
        goToNextStepSuccess: (state, { payload })=>{
            return {
                ...state,
                goToNextStepStatus: "success",
                stepsData: {
                    ...state.stepsData,
                    ...payload
                }
            };
        },
        goToNextStep: (state)=>{
            const nextIndex = getNextIndex(state.steps, state.selectedStep);
            const nextStep = state.steps[nextIndex];
            return {
                ...state,
                goToNextStepStatus: "success",
                selectedStep: nextStep
            };
        },
        setFindTeamMemberStatus: (state, { payload })=>{
            return {
                ...state,
                findTeamMembersStatus: payload.status
            };
        },
        setAssignAndAllocateStatus: (state, { payload })=>{
            return {
                ...state,
                assignAndAllocateStatus: payload.status
            };
        },
        addRowsWithUsersRemovedManually: (state, { payload })=>{
            return {
                ...state,
                rowsWithUsersRemovedManually: state.rowsWithUsersRemovedManually.concat(payload.id)
            };
        },
        removeRowsWithUsersRemovedManually: (state, { payload })=>{
            return {
                ...state,
                rowsWithUsersRemovedManually: state.rowsWithUsersRemovedManually.filter((id)=>id !== payload.id)
            };
        },
        resetRowsWithUsersRemovedManually: (state)=>{
            return {
                ...state,
                rowsWithUsersRemovedManually: []
            };
        }
    }
});
